import { usePerms } from 'contexts/PermsContext';
import RoleForm from 'features/Admin/Roles/RoleManager/components/RoleForm';
import DashboardEditTab from 'features/Admin/Roles/RoleManager/components/RoleTabs/DashboardEditTab';
import DashboardViewTab from 'features/Admin/Roles/RoleManager/components/RoleTabs/DashboardViewTab';
import DetailsTab from 'features/Admin/Roles/RoleManager/components/RoleTabs/DetailsTab';
import PermissionsTab from 'features/Admin/Roles/RoleManager/components/RoleTabs/PermissionsTab';
import useRoleForm from 'features/Admin/Roles/RoleManager/hooks/useRoleForm';
import type { Member, RoleFormData } from 'features/Admin/Roles/types/Roles.types';
import { filterLayoutByPermissions, validateRole } from 'features/Admin/Roles/utils/Roles.utils';
import type { LayoutItem } from 'features/dashboard/types/widgetTypes';
import { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { safeJSONParse } from 'utils/data/parse.utils';
import { notifyErrorToast, notifySuccessToast } from 'utils/notifications/toast.utils';
import { getAnyMemberId, isBrowser, readLocalJSON, toNumberArray } from '../types/guards';

type LocalStorageKeys = {
  formKey?: string;
  memberKey?: string;
  tabKey?: string;
};

type RolePageProps = {
  mode: 'create' | 'edit' | 'view';
  // mutation function that returns a promise (should call .unwrap() internally)
  mutate?: (payload: RoleSubmitPayload) => Promise<void>;
  validatePermissionsOnMount?: string[];
  enableLocalStorage?: boolean;
  localStorageKeys?: LocalStorageKeys;
  tabOptions?: string[];
  tabHeaderOptions?: Array<{ option: string }>;
  leftFirstLabel?: string;
  rightLabel?: string;
  rightFinalLabel?: string;
};

type Checks = { roleName: boolean };

type RoleResult = { type?: string; message?: string } | string | null;

type RoleSubmitPayload = {
  roleName: string;
  members: number[];
  permissions: string;
  layout: string;
  roleId?: string | number;
};

export default function RolePage({
  mode,
  mutate,
  validatePermissionsOnMount,
  enableLocalStorage = false,
  localStorageKeys = {},
  tabOptions: tabOptionsProp,
  tabHeaderOptions,
  leftFirstLabel,
  rightLabel,
  rightFinalLabel,
}: RolePageProps): JSX.Element {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const perms = usePerms();

  const defaultTabs =
    mode === 'view'
      ? ['Role Details', 'Permissions', 'Dashboard View']
      : ['Role Details', 'Permissions', 'Dashboard Edit', 'Dashboard View'];

  const TAB_OPTIONS = tabOptionsProp ?? defaultTabs;

  const [checks, setChecks] = useState<Checks>({ roleName: true });
  const [result, setResult] = useState<RoleResult>('');

  const hasHydratedForm = useRef(false);
  const hasHydratedMembers = useRef(false);
  const hasInitializedTab = useRef(false);

  const {
    permList,
    widgetData,
    formData,
    setFormData,
    membersList,
    setMembersList,
    getCurrentTabIndex,
    updateTabParam,
    buildPermissionsRecord,
    roleId,
  } = useRoleForm({ mode });

  const currentTabIndex = getCurrentTabIndex(TAB_OPTIONS);

  // When a permission that gates a placed widget is turned off, drop that widget
  // from the layout so it disappears from the dashboard preview and is never
  // submitted. filterLayoutByPermissions returns the same reference when nothing
  // changes, so this is a no-op (no re-render) unless a widget is actually pruned.
  useEffect(() => {
    setFormData((prev: RoleFormData) => {
      const layout = prev.layout || [];
      const filtered = filterLayoutByPermissions(layout, widgetData, permList, prev.permissions || []);
      if (filtered === layout) return prev;
      return { ...prev, layout: filtered };
    });
  }, [formData.permissions, formData.layout, widgetData, permList, setFormData]);

  const formKey = localStorageKeys.formKey ?? '';
  const memberKey = localStorageKeys.memberKey ?? '';
  const tabKey = localStorageKeys.tabKey ?? '';

  useEffect(() => {
    if (!validatePermissionsOnMount) return;
    (async () => {
      if (!(await perms.validatePermissions(validatePermissionsOnMount))) {
        navigate('/404');
        return;
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!enableLocalStorage) return;
    if (hasHydratedMembers.current) return;
    if (!Array.isArray(membersList) || membersList.length === 0) return;

    const storedSelectionsRaw = readLocalJSON<Array<string | number>>(memberKey, []);
    const storedSelections = toNumberArray(storedSelectionsRaw);
    const selectedSet = new Set<number>(storedSelections);
    setMembersList((prev: Member[]) =>
      prev.map((item: Member) => {
        const id = getAnyMemberId(item);
        return { ...item, selected: id !== undefined && selectedSet.has(id) };
      }),
    );
    hasHydratedMembers.current = true;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [membersList, setMembersList, enableLocalStorage]);

  useEffect(() => {
    if (!enableLocalStorage || !hasHydratedMembers.current || !isBrowser()) return;
    if (!Array.isArray(membersList)) return;

    const selectedMembers = membersList
      .filter((member: Member) => Boolean(member.selected))
      .map((m: Member) => getAnyMemberId(m))
      .filter((id): id is number => typeof id === 'number');
    localStorage.setItem(memberKey, JSON.stringify(selectedMembers));
  }, [membersList, enableLocalStorage, memberKey]);

  useEffect(() => {
    if (!enableLocalStorage || !hasHydratedForm.current || !isBrowser()) return;

    const payload = {
      roleName: formData.roleName,
      layout: formData.layout,
      permissions: formData.permissions,
    };

    localStorage.setItem(formKey, JSON.stringify(payload));
  }, [formData.roleName, formData.layout, formData.permissions, enableLocalStorage, formKey]);

  useEffect(() => {
    if (!enableLocalStorage) return;
    if (hasHydratedForm.current) return;
    if (!permList.length) return;

    const storedForm = readLocalJSON<Partial<RoleFormData> | null>(formKey, null);

    if (storedForm) {
      const sf = storedForm as Partial<RoleFormData>;
      setFormData((prev: RoleFormData) => ({
        ...prev,
        roleName: typeof sf.roleName === 'string' ? (sf.roleName as string) : '',
        layout: Array.isArray(sf.layout) ? (sf.layout as LayoutItem[]) : [],
        permissions: permList.map((_p, index: number) => {
          const perms = sf.permissions;
          if (Array.isArray(perms)) return Boolean((perms as boolean[])[index]);
          return false;
        }),
      }));
    } else {
      setFormData((prev: RoleFormData) => ({ ...prev, permissions: permList.map(() => false) }));
    }

    hasHydratedForm.current = true;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [permList, enableLocalStorage]);

  useEffect(() => {
    if (!enableLocalStorage) return;
    if (hasInitializedTab.current) return;
    hasInitializedTab.current = true;

    if (!isBrowser()) return;

    const storedTab = localStorage.getItem(tabKey);
    const storedIndex = storedTab
      ? TAB_OPTIONS.findIndex((option) => option.toLowerCase() === storedTab.toLowerCase())
      : -1;

    if (storedIndex !== -1) {
      updateTabParam(storedIndex, TAB_OPTIONS, true);
    } else if (!searchParams.get('tab')) {
      updateTabParam(0, TAB_OPTIONS, true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enableLocalStorage]);

  useEffect(() => {
    if (!enableLocalStorage) return;
    if (!isBrowser()) return;
    localStorage.setItem(tabKey, TAB_OPTIONS[currentTabIndex]);
  }, [currentTabIndex, enableLocalStorage, tabKey]);

  const clearStoredData = ({ clearTab = true } = {}) => {
    if (!isBrowser() || !enableLocalStorage) return;
    if (formKey) localStorage.removeItem(formKey);
    if (memberKey) localStorage.removeItem(memberKey);
    if (clearTab && tabKey) localStorage.removeItem(tabKey);
  };

  useEffect(() => {
    if (!enableLocalStorage) return;
    return () => clearStoredData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enableLocalStorage]);

  const handleClearForm = () => {
    if (enableLocalStorage) clearStoredData({ clearTab: false });
    setFormData((prev: RoleFormData) => ({
      ...prev,
      roleName: '',
      layout: [],
      permissions: permList.map(() => false),
    }));
    setChecks({ roleName: true });
    setMembersList((prev: Member[]) =>
      prev.map((member: Member) => ({ ...member, selected: false })),
    );
    setResult('');
  };

  const renderTab = () => {
    // Mode-based permission checks
    if (mode === 'edit' && !perms.testPermission([8, 9, 10, 11, 12, 13], ['EditRole'])) return null;
    if (mode === 'view' && !perms.testPermission([8, 9, 10, 11, 12, 13], ['ViewRoles']))
      return null;

    const label = TAB_OPTIONS[currentTabIndex] ?? '';

    if (label === 'Role Details') {
      return (
        <DetailsTab
          formData={formData}
          setFormData={setFormData}
          checks={checks}
          membersList={membersList}
          setMembersList={setMembersList}
          widgetData={widgetData}
          permList={permList}
          roleid={mode !== 'create' ? (roleId ?? undefined) : undefined}
          onClearForm={mode === 'create' ? handleClearForm : undefined}
          edit={mode === 'view' ? false : undefined}
        />
      );
    }

    if (label === 'Permissions') {
      return (
        <PermissionsTab
          formData={formData}
          setFormData={setFormData}
          checks={checks}
          widgetData={widgetData}
          permList={permList}
          roleid={mode !== 'create' ? (roleId ?? undefined) : undefined}
          edit={mode === 'view' ? false : undefined}
        />
      );
    }

    if (label === 'Dashboard Edit') {
      if (!perms.testPermission([8, 9, 10, 11, 12, 13], ['EditRoleDashboard'])) return null;
      return (
        <DashboardEditTab
          formData={formData}
          setFormData={setFormData}
          checks={checks}
          widgetData={widgetData}
          permList={permList}
          compactPreview
        />
      );
    }

    if (label === 'Dashboard View') {
      if (!perms.testPermission([8, 9, 10, 11, 12, 13], ['ViewAsRole'])) return null;
      if (mode === 'view')
        return <DashboardViewTab formData={formData} widgetData={widgetData} edit={false} />;
      return <DashboardViewTab formData={formData} widgetData={widgetData} compactPreview />;
    }

    // Fallback to details
    return (
      <DetailsTab
        formData={formData}
        setFormData={setFormData}
        checks={checks}
        membersList={membersList}
        setMembersList={setMembersList}
        widgetData={widgetData}
        permList={permList}
        roleid={mode !== 'create' ? (roleId ?? undefined) : undefined}
        onClearForm={mode === 'create' ? handleClearForm : undefined}
        edit={mode === 'view' ? false : undefined}
      />
    );
  };

  const onSubmit = async () => {
    if (!mutate) return;

    const permissions = buildPermissionsRecord();

    let members: number[] = [];
    if (mode === 'create') {
      members = membersList
        .filter((m: Member) => Boolean(m.selected))
        .map((m: Member) => getAnyMemberId(m))
        .filter((id): id is number => typeof id === 'number');
    } else if (mode === 'edit') {
      members = (membersList || [])
        .filter((filter: Member) => {
          const msListRaw = safeJSONParse(filter?.msRoles, []) as Array<string | number>;
          const msList = Array.isArray(msListRaw)
            ? (msListRaw.map((v) => Number(v)) as number[])
            : [];
          const roleIdNum = parseInt(String(roleId ?? '0'), 10);
          return filter?.selected && !msList.includes(roleIdNum);
        })
        .map((member: Member) => getAnyMemberId(member))
        .filter((id): id is number => typeof id === 'number');
    }

    // Never persist widgets whose gating permissions were turned off — the
    // backend rejects a layout containing widgets the role lacks permission
    // for. Prune against the same predicate the widget selector uses.
    const allowedLayout = filterLayoutByPermissions(
      formData?.layout || [],
      widgetData,
      permList,
      formData?.permissions || [],
    );

    const payload: RoleSubmitPayload = {
      roleName: formData?.roleName || '',
      members,
      permissions: JSON.stringify(permissions),
      layout: JSON.stringify(allowedLayout),
    };

    if (mode === 'edit') payload.roleId = roleId ?? undefined;

    try {
      await mutate(payload);
      setResult({
        type: 'success',
        message: mode === 'create' ? 'Role created successfully' : 'Role updated successfully',
      });
      notifySuccessToast('Role', { isAddOperation: mode === 'create' });
      navigate('/system/rolemanager');
    } catch (error) {
      console.error('Failed to submit role', error);
      notifyErrorToast('Role', { isAddOperation: mode === 'create' });
      const getErrorMessage = (err: Error | { message?: string } | string): string => {
        try {
          if (err instanceof Error) return err.message;
          if (
            err &&
            typeof err === 'object' &&
            'message' in err &&
            typeof (err as { message?: string }).message === 'string'
          )
            return (err as { message?: string }).message ?? '';
        } catch (e) {
          // ignore
        }
        return String(err);
      };
      setResult({ type: 'failed', message: 'Failed: ' + getErrorMessage(error as Error) });
    }
  };

  const onSubmitClick = () => {
    const validation = validateRole(formData);
    setChecks({ roleName: !Boolean(validation.errors.roleName) });
    if (!validation.valid) {
      const messages = Object.values(validation.errors).filter(Boolean) as string[];
      const msgText = messages.length > 0 ? `${messages.join('; ')}` : 'Role validation failed.';
      notifyErrorToast('Role', { isAddOperation: mode === 'create' }, { errorPayload: msgText });
      return;
    }

    onSubmit();
  };

  const onClose = () => navigate('/system/rolemanager');

  const onBack = () => {
    if (currentTabIndex === 0) onClose();
    else updateTabParam(currentTabIndex - 1, TAB_OPTIONS);
  };

  const onNext = () => {
    if (currentTabIndex === TAB_OPTIONS.length - 1) {
      if (mode === 'view') navigate('/system/rolemanager');
      else onSubmitClick();
    } else {
      updateTabParam(currentTabIndex + 1, TAB_OPTIONS);
    }
  };

  return (
    <RoleForm
      tabOptions={TAB_OPTIONS}
      currentTabIndex={currentTabIndex}
      renderTab={renderTab}
      onBack={onBack}
      onNext={onNext}
      result={result}
      tabHeaderOptions={tabHeaderOptions}
      leftFirstLabel={leftFirstLabel}
      rightLabel={rightLabel}
      rightFinalLabel={rightFinalLabel}
    />
  );
}
